/**
 * Time formatting utilities
 */

import { Duration } from '@/types/ai-learn.types';

/**
 * Format duration for display
 * @param time - Duration object with optional hours, minutes and seconds
 * @returns Formatted string (e.g., "45 sec", "2 min", "1 min 45 sec", "1 hr", "1 hr 30 min")
 */
export function formatDuration(time?: Duration | number | any): string {
  try {
    if (!time) return '0 sec';

    let totalSeconds = 0;
    if (typeof time === 'number') {
      totalSeconds = Math.round(time);
    } else if (typeof time === 'string') {
      totalSeconds = Math.round(parseFloat(time) || 0);
    } else if (typeof time === 'object') {
      totalSeconds = Math.round(
        (Number(time.hours) || 0) * 3600 +
        (Number(time.minutes) || 0) * 60 +
        (Number(time.seconds) || 0)
      );
    }

    if (isNaN(totalSeconds) || totalSeconds <= 0) return '0 sec';

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const parts: string[] = [];
    if (hours > 0) parts.push(`${hours} hr`);
    if (minutes > 0) parts.push(`${minutes} min`);
    if (seconds > 0) parts.push(`${seconds} sec`);
    return parts.join(' ') || '0 sec';
  } catch (err) {
    console.error('Error formatting duration:', err);
    return '0 sec';
  }
}

/**
 * Convert minutes (number) to Duration object
 * @param totalMinutes - Total minutes as a number
 * @returns Duration object with hours and minutes
 */
export function minutesToDuration(totalMinutes: number): Duration {
  if (!totalMinutes || totalMinutes < 1) {
    return { hours: 0, minutes: totalMinutes || 0 };
  }

  if (totalMinutes >= 60) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return { hours, minutes };
  }

  return { hours: 0, minutes: totalMinutes };
}

/**
 * Convert seconds (number) to Duration object
 */
export function secondsToDuration(totalSeconds: number): Duration {
  const sec = Math.round(totalSeconds || 0);
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);
  const seconds = sec % 60;
  return { hours, minutes, seconds };
}

/**
 * Extract actual duration from content item, dynamically calculating sum of scene
 * durations if scenes exist and have rendered audio/video lengths.
 */
export function getContentActualDuration(item: any): Duration | null {
  try {
    if (!item) return null;

    // 1. If item has explicit merged video duration or estimated_time, use it first
    if (item.duration) {
      const dur = typeof item.duration === 'number' ? secondsToDuration(item.duration) : item.duration;
      const totalSec = (dur.hours || 0) * 3600 + (dur.minutes || 0) * 60 + (dur.seconds || 0);
      if (totalSec > 0) return dur;
    }
    if (item.estimated_time) {
      const dur = typeof item.estimated_time === 'number' ? secondsToDuration(item.estimated_time * 60) : item.estimated_time;
      const totalSec = (dur.hours || 0) * 3600 + (dur.minutes || 0) * 60 + (dur.seconds || 0);
      if (totalSec > 0) return dur;
    }

    // 2. Fall back to summing scene-level durations if item duration is missing
    if (Array.isArray(item.scenes) && item.scenes.length > 0) {
      let totalSceneSeconds = 0;
      let hasValidSceneDurations = false;
      for (const scene of item.scenes) {
        let sceneSec = 0;
        if (typeof scene?.duration === 'number' && scene.duration > 0) {
          sceneSec = scene.duration;
        } else if (typeof scene?.media_generation?.audio_duration === 'number') {
          sceneSec = scene.media_generation.audio_duration;
        } else if (typeof scene?.media_generation?.duration === 'number') {
          sceneSec = scene.media_generation.duration;
        } else if (Array.isArray(scene?.synchronized_captions?.words) && scene.synchronized_captions.words.length > 0) {
          const lastWord = scene.synchronized_captions.words[scene.synchronized_captions.words.length - 1];
          if (lastWord && typeof lastWord.end_time === 'number') {
            sceneSec = lastWord.end_time;
          }
        }
        if (sceneSec > 0) {
          totalSceneSeconds += sceneSec;
          hasValidSceneDurations = true;
        }
      }
      if (hasValidSceneDurations && totalSceneSeconds > 0) {
        return secondsToDuration(totalSceneSeconds);
      }
    }
  } catch (err) {
    console.error('Error getting actual content duration:', err);
  }

  return null;
}
